import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { Accept } from 'react-dropzone';
import styled from 'styled-components';

import { Checkbox } from '@creditornot/cb-checkbox';
import { Modal } from '@creditornot/cbt-modal';
import { Button } from 'common/components/cookbook/Button';
import { FileInput } from 'common/components/cookbook/FileInput';
import { ModalFooter } from 'common/components/cookbook/Modals/ModalFooter';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useToast } from 'common/hooks/useToast';
import { useApiQueryClient } from 'common/query/useApiQueryClient';
import { confirmOverridesDraft, uploadOverrideCsv } from 'features/override-management/api';
import { OverridesDraft } from 'features/override-management/api-types';
import {
  OVERRIDE_MANAGEMENT_KEY_ROOT,
  useOverrideDraftSummaryQuery,
} from 'features/override-management/query';

import { ExampleFileDownloadButton } from './ExampleFileDownloadButton';
import OverrideFileFormat from './OverrideFileFormat';
import { ReviewSummaryStep } from './ReviewSummaryStep';
import { UploadOverrideWizard, UploadStage } from './UploadOverrideWizard';

type Props = {
  isOpen: boolean;
  onSuccess: () => void;
  onDismiss: () => void;
};

const WizardContainer = styled(Stack)`
  padding: 16px;
`;

const Content = styled(Stack)`
  width: 100%;
  max-width: 512px;
`;

const FILE_INPUT_ACCEPT_CSV: Accept = {
  'text/csv': ['.csv'],
};

export function UploadOverrideCSVModal({ isOpen, onDismiss: _onDismiss, onSuccess }: Props) {
  const [stage, setStage] = useState<UploadStage>('upload');
  const [draftId, setDraftId] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [deleteExisting, setDeleteExisting] = useState(false);

  const format = useFormatMessage();
  const [countryCode] = useSelectedCountryCodes();
  const { addErrorToast } = useToast();
  const queryClient = useApiQueryClient();

  const { mutate: uploadCsv, isPending: uploadCsvPending } = useMutation<
    OverridesDraft,
    AxiosError,
    File
  >({
    mutationFn: (file) => uploadOverrideCsv(countryCode, file),
    onSuccess: (result) => {
      setDraftId(result.id);
      setStage('confirm');
      void queryClient.invalidateQueries({ queryKey: [OVERRIDE_MANAGEMENT_KEY_ROOT] });
    },
    onError: addErrorToast,
  });

  const { mutate: confirmDraft, isPending: confirmPending } = useMutation<
    void,
    AxiosError,
    { id: string; deleteExistingOverrides: boolean }
  >({
    mutationFn: ({ id, deleteExistingOverrides }) =>
      confirmOverridesDraft(id, countryCode, { deleteExistingOverrides }),
    onSuccess: () => {
      onSuccess();
      void queryClient.invalidateQueries({ queryKey: [OVERRIDE_MANAGEMENT_KEY_ROOT] });
      resetState();
    },
    onError: addErrorToast,
  });

  const {
    data: summary,
    isLoading,
    error,
  } = useOverrideDraftSummaryQuery(draftId ?? '', countryCode, Boolean(draftId));

  function resetState() {
    setStage('upload');
    setDraftId(null);
    setFiles([]);
  }

  function onDismiss() {
    _onDismiss();
    resetState();
  }

  function handleNextButtonClick() {
    if (stage === 'upload') {
      uploadCsv(files[0]);
    }
  }

  function changeDeleteExisting() {
    setDeleteExisting(!deleteExisting);
  }

  const fileNames = useMemo(() => files.map((file) => file.name), [files]);
  const summaryItems = summary?.items ?? [];

  return (
    <Modal
      sizePreset="centered:large"
      open={isOpen}
      onDismiss={onDismiss}
      title={format('common.upload_csv_file')}
      FooterComponent={ModalFooter}
      footer={
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap="regularMedium"
          style={{ width: '100%' }}
        >
          {stage === 'confirm' && (
            <Checkbox
              checked={deleteExisting}
              disabled={summaryItems.length === 0}
              onChange={changeDeleteExisting}
              style={{ flexShrink: 0 }}
            >
              {format('forecasting.delete_overrides_not_in_the_file')}
            </Checkbox>
          )}
          <Stack
            direction="row"
            gap="regularMedium"
            alignItems="center"
            justifyContent="flex-end"
            style={{ width: '100%' }}
          >
            <Button onClick={onDismiss}>{format('common.cancel')}</Button>
            {stage === 'confirm' && draftId ? (
              <Button
                type="submit"
                variant="primary"
                onClick={() =>
                  confirmDraft({ id: draftId, deleteExistingOverrides: deleteExisting })
                }
                loading={confirmPending}
                disabled={confirmPending}
              >
                {format('forecasting.upload_and_finish')}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleNextButtonClick}
                loading={uploadCsvPending}
                disabled={uploadCsvPending}
              >
                {format('common.next')}
              </Button>
            )}
          </Stack>
        </Stack>
      }
    >
      <Stack gap="regularMedium" direction="column" alignItems="flex-start">
        <Stack alignItems="center" style={{ width: '100%' }}>
          <WizardContainer>
            <UploadOverrideWizard stage={stage} setStage={setStage} />
          </WizardContainer>
          {stage === 'upload' && (
            <Content gap="regularLarge">
              <ExampleFileDownloadButton />
              <OverrideFileFormat />
              <FileInput
                onDrop={setFiles}
                id="overridesCsv"
                accept={FILE_INPUT_ACCEPT_CSV}
                selectedFiles={fileNames}
                buttonText={format('common.choose_csv_file')}
              />
            </Content>
          )}
        </Stack>
        {stage === 'confirm' && draftId && (
          <ReviewSummaryStep isLoading={isLoading} summaryItems={summaryItems} error={error} />
        )}
      </Stack>
    </Modal>
  );
}
